import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CircleDashed,
  FlaskConical,
  MessageSquare,
  Pill,
} from 'lucide-react';

import type { ClinicianSuggestionViewT, JsonValue } from '@lotushealth/lotushealth_js_sdk/schemas';

import { lotusDarkLogo } from '@/shared/assets';
import { statusToneClassName } from '@/shared/ui/statusTone';

import { fetchPatientSuggestions } from '../api/fetchPatientSuggestions';

interface ResolvedSuggestionsSectionProps {
  userId: string;
}

const EMPTY_SUGGESTIONS: ClinicianSuggestionViewT[] = [];

type ResolvedPlanItem = { id: string; subtitle: string; instruction: string; kind: string };

function isRecord(value: JsonValue | undefined): value is { [key: string]: JsonValue } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function approvedPlanItem(value: JsonValue): ResolvedPlanItem | null {
  if (!isRecord(value) || typeof value.id !== 'string' || !isRecord(value.proposal)) {
    return null;
  }
  const { proposal } = value;
  if (
    typeof proposal.subtitle !== 'string' ||
    typeof proposal.instruction !== 'string' ||
    typeof proposal.type !== 'string'
  ) {
    return null;
  }
  return {
    id: value.id,
    subtitle: proposal.subtitle,
    instruction: proposal.instruction,
    kind: proposal.type,
  };
}

function actionKind(kind: string): { label: string; Icon: typeof Pill } {
  switch (kind) {
    case 'request_prescription':
      return { label: 'Prescription', Icon: Pill };
    case 'request_lab':
      return { label: 'Lab request', Icon: FlaskConical };
    default:
      return { label: 'Discuss', Icon: MessageSquare };
  }
}

function dateParts(value: string): { short: string; month: string } {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { short: '—', month: 'Date unavailable' };
  }
  return {
    short: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    month: date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
  };
}

function ResolvedSuggestionRow({
  suggestion,
  expanded,
  onToggle,
}: {
  suggestion: ClinicianSuggestionViewT;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [contextExpanded, setContextExpanded] = useState(false);
  const lifecycle = suggestion.lifecycle;
  if (lifecycle.type !== 'approved') {
    return null;
  }

  const planById = new Map(
    lifecycle.plan
      .map(approvedPlanItem)
      .filter((item): item is ResolvedPlanItem => item !== null)
      .map((item) => [item.id, item])
  );
  const actions = lifecycle.item_results.map((result) => ({
    result,
    item: planById.get(result.item_id),
    created: result.type === 'materialized',
  }));
  const date = dateParts(lifecycle.approved_at).short;
  const title =
    'title' in suggestion.draft ? suggestion.draft.title : 'Suggestion details unavailable';
  const context = 'cortex_context' in suggestion.draft ? suggestion.draft.cortex_context : '';

  return (
    <article className="border-b border-border last:border-b-0">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="grid w-full grid-cols-[3rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 px-4 py-4 text-left hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring md:grid-cols-[5rem_minmax(0,1.15fr)_minmax(0,1.35fr)_minmax(8rem,auto)_1rem] md:gap-x-4 md:px-5"
      >
        <span className="row-span-2 text-[12px] text-muted-foreground md:row-span-1">{date}</span>
        <span className="min-w-0">
          <span className="block truncate text-[13px] leading-snug text-foreground">{title}</span>
          <span className="mt-0.5 block truncate text-[11px] leading-snug text-muted-foreground">
            {suggestion.placement?.provisional_problem_label ?? 'Clinical suggestion'}
          </span>
        </span>
        <span className="col-start-2 flex min-w-0 flex-col gap-1 md:col-start-auto">
          {actions.length ? (
            actions.map(({ result, item }) => {
              const { Icon } = actionKind(item?.kind ?? 'discuss');
              return (
                <span
                  key={result.item_id}
                  className="flex min-w-0 items-center gap-2 text-[12px] leading-snug"
                >
                  <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="truncate">{item?.subtitle ?? 'Resulting action'}</span>
                </span>
              );
            })
          ) : (
            <span className="text-[12px] text-muted-foreground">No resulting actions recorded</span>
          )}
        </span>
        <span className="col-start-2 flex items-center justify-between gap-3 text-[12px] text-foreground/80 md:col-start-auto md:justify-end">
          <span>Clinician</span>
          {expanded ? (
            <ChevronUp className="size-4 text-muted-foreground md:hidden" aria-hidden="true" />
          ) : (
            <ChevronDown className="size-4 text-muted-foreground md:hidden" aria-hidden="true" />
          )}
        </span>
        {expanded ? (
          <ChevronUp className="hidden size-4 text-muted-foreground md:block" aria-hidden="true" />
        ) : (
          <ChevronDown
            className="hidden size-4 text-muted-foreground md:block"
            aria-hidden="true"
          />
        )}
      </button>

      {expanded && (
        <div className="grid gap-6 border-t border-border bg-muted/30 px-4 py-4 md:grid-cols-2 md:gap-8 md:px-[7.5rem] md:py-4">
          <section className="min-w-0">
            <h4 className="mb-2 text-[12px] font-medium text-muted-foreground">What was done</h4>
            <ul className="overflow-hidden rounded-xl border border-border bg-background">
              {actions.length ? (
                actions.map(({ result, item, created }, index) => {
                  const { Icon } = actionKind(item?.kind ?? 'discuss');
                  return (
                    <li
                      key={result.item_id}
                      className={`flex min-w-0 items-start gap-3 px-3 py-3 ${index ? 'border-t border-border' : ''}`}
                    >
                      <Icon
                        className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] leading-snug">
                          {item?.subtitle ??
                            (created ? 'Resulting action' : 'Action could not be created')}
                        </p>
                        {item?.instruction && (
                          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                            {item.instruction}
                          </p>
                        )}
                      </div>
                      {created ? (
                        <span
                          className={`${statusToneClassName('success')} shrink-0 rounded-full px-2 py-1 text-[10px] font-medium`}
                        >
                          Done
                        </span>
                      ) : (
                        <span className="flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground">
                          <CircleDashed className="size-3.5" aria-hidden="true" /> Not created
                        </span>
                      )}
                    </li>
                  );
                })
              ) : (
                <li className="px-3 py-3 text-[12px] text-muted-foreground">
                  No resulting actions recorded.
                </li>
              )}
            </ul>
          </section>

          <section className="min-w-0">
            <h4 className="mb-2 flex items-center gap-2 text-[12px] font-medium text-muted-foreground">
              <img
                src={lotusDarkLogo}
                alt=""
                className="size-4 object-contain invert dark:invert-0"
                aria-hidden="true"
              />
              Why Cortex suggested this
            </h4>
            <div className="relative">
              <div
                className={`overflow-hidden text-[12px] leading-[1.65] text-foreground/80 ${contextExpanded ? '' : 'max-h-32'}`}
              >
                <p className="whitespace-pre-wrap">{context || 'Clinical context unavailable.'}</p>
              </div>
              {!contextExpanded && context.length > 0 && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-muted/30 to-transparent"
                />
              )}
            </div>
            {context.length > 0 && (
              <button
                type="button"
                aria-expanded={contextExpanded}
                onClick={() => setContextExpanded((value) => !value)}
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {contextExpanded ? 'Show less' : 'Show full summary'}
                {contextExpanded ? (
                  <ChevronUp className="size-3.5" aria-hidden="true" />
                ) : (
                  <ChevronDown className="size-3.5" aria-hidden="true" />
                )}
              </button>
            )}
          </section>
        </div>
      )}
    </article>
  );
}

export function ResolvedSuggestionsSection({ userId }: ResolvedSuggestionsSectionProps) {
  const [expandedId, setExpandedId] = useState<string | null | undefined>(undefined);
  const query = useQuery({
    queryKey: ['/greenfield/care', userId, 'suggestions'],
    queryFn: ({ signal }) => fetchPatientSuggestions(userId, signal),
    enabled: userId.length > 0,
  });
  const suggestions = useMemo(
    () =>
      (query.data ?? EMPTY_SUGGESTIONS)
        .filter((suggestion) => suggestion.lifecycle.type === 'approved')
        .sort((a, b) => {
          const aTime = a.lifecycle.type === 'approved' ? Date.parse(a.lifecycle.approved_at) : 0;
          const bTime = b.lifecycle.type === 'approved' ? Date.parse(b.lifecycle.approved_at) : 0;
          return bTime - aTime;
        }),
    [query.data]
  );
  const grouped = useMemo(() => {
    const groups = new Map<string, ClinicianSuggestionViewT[]>();
    for (const suggestion of suggestions) {
      const value =
        suggestion.lifecycle.type === 'approved' ? suggestion.lifecycle.approved_at : '';
      const month = dateParts(value).month;
      groups.set(month, [...(groups.get(month) ?? []), suggestion]);
    }
    return [...groups.entries()];
  }, [suggestions]);
  const selectedExpandedId = expandedId === undefined ? suggestions[0]?.actionId : expandedId;

  if (query.isPending) {
    return <div className="p-6 text-sm text-muted-foreground">Loading resolved suggestions…</div>;
  }
  if (query.isError) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Resolved suggestions could not be loaded. Try again later.
      </div>
    );
  }
  if (suggestions.length === 0) {
    return (
      <section
        aria-label="Resolved suggestions"
        className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground"
      >
        No resolved suggestions for this patient.
      </section>
    );
  }

  return (
    <section aria-label="Resolved suggestions" className="flex flex-col gap-5">
      <div className="grid grid-cols-[3rem_minmax(0,1fr)] gap-x-3 border-b border-border px-4 pb-2 text-[11px] text-muted-foreground md:grid-cols-[5rem_minmax(0,1.15fr)_minmax(0,1.35fr)_minmax(8rem,auto)_1rem] md:gap-x-4 md:px-5">
        <span className="col-span-2 md:col-span-2">Request</span>
        <span className="hidden md:block">What was done</span>
        <span className="hidden text-right md:block">Resolved by</span>
      </div>
      {grouped.map(([month, monthSuggestions]) => (
        <section key={month}>
          <h3 className="mb-2 px-0.5 text-[12px] font-medium text-foreground/80">{month}</h3>
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {monthSuggestions.map((suggestion) => (
              <ResolvedSuggestionRow
                key={suggestion.actionId}
                suggestion={suggestion}
                expanded={selectedExpandedId === suggestion.actionId}
                onToggle={() =>
                  setExpandedId((current) =>
                    (current === undefined ? suggestions[0]?.actionId : current) ===
                    suggestion.actionId
                      ? null
                      : suggestion.actionId
                  )
                }
              />
            ))}
          </div>
        </section>
      ))}
    </section>
  );
}
